import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, Clock, Star, Lock, CheckCircle, Play, Award,
  Zap, Shield, ChevronRight, Users, TrendingUp, Crown,
  CreditCard, ShoppingCart, Flame, ArrowRight, FileCheck,
  Send, MessageSquare, Lightbulb
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import Footer from '../components/Footer';
import PublicWishBoard from '../components/PublicWishBoard';
import CourseWishBanner from '../components/CourseWishBanner';
import PricingTiersGrid from '../components/PricingTiersGrid';

import { apiFetch } from '../services/api';
const CATEGORY_CONFIG = {
  basis: { name: "Basis-Schulungen", icon: "📋", description: "Pflichtschulungen für jeden Elektrobetrieb", color: "from-yellow-500/20 to-yellow-600/10", border: "border-yellow-500/30" },
  pv: { name: "Photovoltaik", icon: "☀️", description: "Sicher installieren & anschließen", color: "from-orange-500/20 to-orange-600/10", border: "border-orange-500/30" },
  smart_home: { name: "Smart Home", icon: "🏠", description: "Gebäudeautomation & Vernetzung", color: "from-blue-500/20 to-blue-600/10", border: "border-blue-500/30" },
  buero: { name: "Büro & Organisation", icon: "💼", description: "Effizienter arbeiten & dokumentieren", color: "from-green-500/20 to-green-600/10", border: "border-green-500/30" },
  ki_handwerk: { name: "KI im Handwerk", icon: "🤖", description: "Künstliche Intelligenz produktiv nutzen", color: "from-purple-500/20 to-purple-600/10", border: "border-purple-500/30" } };

const ModuleCard = ({ module, progress, onClick, onBuy }) => {
  const isLocked = !module.has_access && !module.is_free_trial;
  const isFreeTrial = module.is_free_trial;
  const isCompleted = progress?.final_quiz_passed;
  const progressPercent = progress?.progress_percent || 0;

  return (
    <Card 
      className={`bg-gray-900/60 border-gray-800 hover:border-yellow-500/50 transition-all duration-300 cursor-pointer group hover:-translate-y-1 hover:shadow-xl hover:shadow-yellow-500/5 ${
        isLocked ? 'opacity-80' : ''
      }`}
      onClick={() => isLocked ? (onBuy && onBuy(module)) : onClick(module.id)}
      data-testid={`module-card-${module.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="text-4xl mb-2">{module.icon}</div>
          <div className="flex gap-2">
            {isCompleted && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle className="w-3 h-3 mr-1" /> Bestanden
              </Badge>
            )}
            {isFreeTrial && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse">
                <Flame className="w-3 h-3 mr-1" /> 50% Gratis
              </Badge>
            )}
            {isLocked && (
              <Badge className="bg-gray-500/20 text-gray-400 border-0">
                <Lock className="w-3 h-3 mr-1" /> Premium
              </Badge>
            )}
            {module.is_addon && !isLocked && (
              <Badge className="bg-purple-500/20 text-purple-400 border-0">
                <Star className="w-3 h-3 mr-1" /> Add-On
              </Badge>
            )}
          </div>
        </div>
        <CardTitle className="text-white group-hover:text-yellow-400 transition-colors text-lg leading-tight">
          {module.title}
        </CardTitle>
        <p className="text-gray-400 text-sm line-clamp-2 mt-1">{module.description}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {module.duration_minutes} Min.
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" /> {module.difficulty}
            </span>
            <span className="flex items-center gap-1">
              <FileCheck className="w-3.5 h-3.5" /> Zertifikat
            </span>
          </div>

          {progressPercent > 0 && !isLocked && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Fortschritt</span>
                <span className="text-yellow-500">{progressPercent.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-yellow-500 to-green-500 transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          )}

          {module.legal_references?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {module.legal_references.slice(0, 3).map((ref, idx) => (
                <Badge key={idx} variant="outline" className="text-xs border-gray-700 text-gray-500">
                  {ref}
                </Badge>
              ))}
            </div>
          )}

          <Button 
            className={`w-full font-bold ${
              isLocked 
                ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-400' 
                : isCompleted
                  ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                  : isFreeTrial
                    ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-400'
                    : progressPercent > 0
                      ? 'bg-yellow-600 text-black hover:bg-yellow-500'
                      : 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-400'
            }`}
            data-testid={`module-cta-${module.id}`}
          >
            {isLocked ? (
              <><ShoppingCart className="w-4 h-4 mr-2" /> Jetzt freischalten</>
            ) : isCompleted ? (
              <><CheckCircle className="w-4 h-4 mr-2" /> Wiederholen</>
            ) : isFreeTrial ? (
              <><Play className="w-4 h-4 mr-2" /> Kostenlos testen</>
            ) : progressPercent > 0 ? (
              <><Play className="w-4 h-4 mr-2" /> Fortsetzen</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Starten</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CoursesOverview = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alle');
  const [searchParams] = useSearchParams();
  const brancheFilter = searchParams.get('branche') || 'alle';
  const [userProgress, setUserProgress] = useState({});
  const [wishName, setWishName] = useState('');
  const [wishEmail, setWishEmail] = useState('');
  const [wishText, setWishText] = useState('');
  const [wishSending, setWishSending] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/learning/modules`);
      const data = await response.json();
      if (data && data.success && data.modules) {
        setModules(data.modules);
      }
    } catch (error) {
      console.error('Error loading modules:', error);
      toast({ title: "Fehler", description: "Kurse konnten nicht geladen werden", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (moduleId) => {
    navigate(`/kurs/${moduleId}`);
  };

  const handleBuyModule = async (module) => {
    const catMap = { basis: 'basis', pv: 'pv', smart_home: 'smart_home', buero: 'buero', ki_handwerk: 'ki_handwerk' };
    const packageType = catMap[module.category] || 'komplett';
    try {
      const url = `${API_URL}/api/learning/checkout/${packageType}`;
      const res = await apiFetch(url);
      const data = await res.json();
      if (data.success && data.checkout_url) {
        window.open(data.checkout_url, '_blank');
      } else {
        toast({ title: "Hinweis", description: data.message || "Bitte kontaktieren Sie uns unter smartvolt-academy@gmx.net", variant: "default" });
      }
    } catch {
      toast({ title: "Fehler", description: "Checkout konnte nicht geladen werden", variant: "destructive" });
    }
  };

  const handleWishSubmit = async (e) => {
    e.preventDefault();
    if (!wishText.trim()) return;
    setWishSending(true);
    try {
      const res = await apiFetch(`${API_URL}/api/learning/course-wish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: wishName, email: wishEmail, wish: wishText, category: activeTab !== 'alle' ? activeTab : '' })
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: "Vielen Dank!", description: "Ihr Kurswunsch wurde gespeichert. Wir melden uns bei Ihnen." });
        setWishName(''); setWishEmail(''); setWishText('');
      }
    } catch {
      toast({ title: "Fehler", description: "Kurswunsch konnte nicht gesendet werden", variant: "destructive" });
    } finally {
      setWishSending(false);
    }
  };

  // Kategorie-Filter (Tab) und Branchen-Filter (URL-Param) kombinieren
  const filteredModules = modules.filter((m) => {
    if (activeTab !== 'alle' && m.category !== activeTab) return false;
    if (brancheFilter !== 'alle') {
      // Branchen-universelle Kurse (BetrSichV) UND branchenspezifische Kurse einschließen
      const isBranchenUniversal = m.branchen_universal || m.category === 'allgemein';
      const matchesBranche = m.branche === brancheFilter || m.category === brancheFilter;
      if (!isBranchenUniversal && !matchesBranche) return false;
    }
    return true;
  });

  const basisCount = modules.filter(m => !m.is_addon).length;
  const addonCount = modules.filter(m => m.is_addon).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Lade Kurse...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Helmet>
        <title>Schulungskatalog - SmartVolt Academy</title>
        <meta name="description" content="16 Kurse für Elektrofachkräfte: DGUV V1, VDE 0105-100, Photovoltaik, Smart Home, KI im Handwerk. Pflichtschulungen online absolvieren." />
      </Helmet>

      {/* Hero Header */}
      <header className="relative overflow-hidden border-b border-yellow-500/20">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5" />
        <div className="container mx-auto px-4 py-8 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Schulungskatalog
              </h1>
              <p className="text-gray-400 mt-2 max-w-xl">
                {modules.length} zertifizierte Kurse für Elektrofachkräfte. DGUV-konform, praxisnah und sofort verfügbar.
              </p>
              <div className="flex items-center gap-4 mt-3">
                <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 px-3 py-1">
                  <Shield className="w-3.5 h-3.5 mr-1" /> {basisCount} Pflichtschulungen
                </Badge>
                <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 px-3 py-1">
                  <Star className="w-3.5 h-3.5 mr-1" /> {addonCount} Spezial-Module
                </Badge>
              </div>
            </div>
            <Button 
              onClick={() => handleBuyModule({ category: 'komplett' })}
              className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold px-6 hover:from-yellow-500 hover:to-yellow-400 shadow-lg shadow-yellow-500/20"
              data-testid="header-buy-komplett-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> Komplett-Zugang
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Testzugang-Banner */}
        <div className="mb-8 bg-gradient-to-r from-yellow-500/10 to-green-500/10 border border-yellow-500/30 rounded-xl p-5 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="free-trial-banner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-white font-bold">14-Tage-Testzugang für Ihren Betrieb</h3>
              <p className="text-gray-400 text-sm">Alle Kurse freigeschaltet · heute 0,00 € · jederzeit kündbar</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/#pricing')}
            className="bg-yellow-500 text-black font-bold hover:bg-yellow-400 whitespace-nowrap"
            data-testid="free-trial-cta-btn"
          >
            <Play className="w-4 h-4 mr-2" /> Testzugang starten
          </Button>
        </div>

        {/* Wunsch-Kurs Banner (prominent oberhalb der Kategorien) */}
        <div className="mb-8">
          <CourseWishBanner />
        </div>

        {brancheFilter !== 'alle' && (
          <div
            className="mb-6 flex items-center gap-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-4 py-3"
            data-testid="branche-filter-active-banner"
          >
            <span className="text-2xl" aria-hidden="true">🎯</span>
            <div className="flex-1">
              <p className="text-sm text-yellow-200">
                <strong>Branchen-Filter aktiv:</strong> Zeige BetrSichV-Universal-Kurse + Spezialkurse für{' '}
                <span className="uppercase font-bold text-yellow-300">{brancheFilter}</span>.
              </p>
            </div>
            <button
              onClick={() => navigate('/kurse')}
              className="text-yellow-300 hover:text-yellow-200 text-sm font-semibold underline"
              data-testid="clear-branche-filter"
            >
              Filter zurücksetzen
            </button>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="category-tabs">
          <button
            onClick={() => setActiveTab('alle')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'alle' ? 'bg-yellow-500 text-black' : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
            data-testid="tab-alle"
          >
            Alle Kurse ({modules.length})
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
            const count = modules.filter(m => m.category === key).length;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === key ? 'bg-yellow-500 text-black' : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
                data-testid={`tab-${key}`}
              >
                <span className="mr-1">{config.icon}</span> {config.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Category Description */}
        {activeTab !== 'alle' && CATEGORY_CONFIG[activeTab] && (
          <div className={`mb-8 p-5 bg-gradient-to-r ${CATEGORY_CONFIG[activeTab].color} rounded-xl border ${CATEGORY_CONFIG[activeTab].border}`}>
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <span className="text-2xl">{CATEGORY_CONFIG[activeTab].icon}</span>
              {CATEGORY_CONFIG[activeTab].name}
            </h2>
            <p className="text-gray-300 mt-1">{CATEGORY_CONFIG[activeTab].description}</p>
          </div>
        )}

        {/* Module Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="module-grid">
          {filteredModules.map(module => (
            <ModuleCard 
              key={module.id}
              module={module}
              progress={userProgress[module.id]}
              onClick={handleModuleClick}
              onBuy={handleBuyModule}
            />
          ))}
        </div>

        {filteredModules.length === 0 && !loading && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Keine Kurse in dieser Kategorie</p>
          </div>
        )}

        {/* Komplett-Paket CTA */}
        {activeTab === 'alle' && (
          <div className="mt-12 bg-gradient-to-r from-yellow-900/20 via-yellow-800/10 to-yellow-900/20 rounded-2xl p-8 border border-yellow-500/20 text-center" data-testid="komplett-cta">
            <Crown className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Komplett-Paket: Alle {modules.length} Kurse</h3>
            <p className="text-gray-400 max-w-lg mx-auto mb-6">
              Unbegrenzter Zugang zu allen Basis-Schulungen und Spezial-Modulen. 
              Inklusive Zertifikate, KI-Moderatorin Lisa und automatischer Dokumentation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold px-8 py-3 text-lg hover:from-yellow-500 hover:to-yellow-400 shadow-lg shadow-yellow-500/20"
                onClick={() => handleBuyModule({ category: 'komplett' })}
                data-testid="buy-komplett-bottom-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" /> Komplett-Zugang sichern
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-sm text-gray-500">
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> DGUV-konform</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Sofort verfügbar</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Inkl. Zertifikate</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Monatlich kündbar</span>
            </div>
          </div>
        )}
        {/* Kurswunsch-Formular */}
        <div className="mt-12 bg-gray-900/60 rounded-2xl p-8 border border-gray-800" data-testid="course-wish-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Kurs nicht dabei?</h3>
              <p className="text-gray-400 text-sm">Wünschen Sie sich einen Kurs — wir setzen ihn für Sie um.</p>
            </div>
          </div>
          <form onSubmit={handleWishSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Ihr Name"
                value={wishName}
                onChange={e => setWishName(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                data-testid="wish-name-input"
              />
              <input
                type="email"
                placeholder="E-Mail (optional)"
                value={wishEmail}
                onChange={e => setWishEmail(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                data-testid="wish-email-input"
              />
            </div>
            <textarea
              placeholder="Welchen Kurs wünschen Sie sich? (z.B. Thema, Zielgruppe, Normen...)"
              value={wishText}
              onChange={e => setWishText(e.target.value)}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none resize-none"
              data-testid="wish-text-input"
              required
            />
            <Button
              type="submit"
              disabled={wishSending || !wishText.trim()}
              className="bg-yellow-600 text-black font-bold hover:bg-yellow-500 disabled:opacity-50"
              data-testid="wish-submit-btn"
            >
              {wishSending ? 'Wird gesendet...' : <><Send className="w-4 h-4 mr-2" /> Kurswunsch absenden</>}
            </Button>
          </form>
        </div>
      </main>

      {/* Preise & Tiers */}
      <PricingTiersGrid />

      {/* Öffentliches Wunsch-Board — transparente Roadmap */}
      <PublicWishBoard isAuthenticated={false} />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CoursesOverview;
